import { useEffect } from 'react'
import { Link } from 'react-router-dom'
import { useDealStore } from '@/stores/deal-store'
import { useCriteriaStore } from '@/stores/criteria-store'
import { calculateDealMetrics, evaluateGoNoGo } from '@/lib/schema'
import { formatCurrency, formatPercent } from '@/lib/formulas'
import { PlusCircle, TrendingUp, TrendingDown, AlertCircle } from 'lucide-react'
import { clsx } from 'clsx'

export function DealsPage() {
  const { deals, isLoading, error, fetchDeals } = useDealStore()
  const { criteria } = useCriteriaStore()

  useEffect(() => {
    fetchDeals()
  }, [fetchDeals])

  if (isLoading && deals.length === 0) {
    return (
      <div className="flex min-h-[400px] items-center justify-center">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary-500 border-t-transparent" />
      </div>
    )
  }

  if (error) {
    return (
      <div className="rounded-xl border border-danger-200 bg-danger-50 p-6 text-center">
        <p className="text-danger-700">{error}</p>
        <button
          onClick={() => fetchDeals()}
          className="mt-4 rounded-lg bg-danger-600 px-4 py-2 text-sm font-medium text-white hover:bg-danger-700"
        >
          Try Again
        </button>
      </div>
    )
  }

  return (
    <div>
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h1>Deals</h1>
          <p className="mt-1 text-slate-500">
            {deals.length} {deals.length === 1 ? 'deal' : 'deals'} in your pipeline
          </p>
        </div>
        <Link
          to="/deals/new"
          className="flex items-center gap-2 rounded-lg bg-primary-600 px-4 py-2 font-medium text-white hover:bg-primary-700"
        >
          <PlusCircle className="h-4 w-4" />
          New Deal
        </Link>
      </div>

      {deals.length === 0 ? (
        <div className="rounded-xl border-2 border-dashed border-slate-200 p-12 text-center">
          <TrendingUp className="mx-auto h-12 w-12 text-slate-300" />
          <h3 className="mt-4 text-lg font-medium text-slate-900">No deals yet</h3>
          <p className="mt-2 text-slate-500">Get started by analyzing your first property.</p>
          <Link
            to="/deals/new"
            className="mt-4 inline-flex items-center gap-2 rounded-lg bg-primary-600 px-4 py-2 font-medium text-white hover:bg-primary-700"
          >
            <PlusCircle className="h-4 w-4" />
            Add Your First Deal
          </Link>
        </div>
      ) : (
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {deals.map((deal) => {
            const metrics = calculateDealMetrics(deal)
            const goNoGo = criteria ? evaluateGoNoGo(metrics, criteria) : null

            return (
              <Link
                key={deal.id}
                to={`/deals/${deal.id}`}
                className="group rounded-xl border border-slate-200 bg-white p-5 shadow-sm transition-all hover:border-primary-200 hover:shadow-md"
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1 min-w-0">
                    <h3 className="truncate font-medium text-slate-900 group-hover:text-primary-600">
                      {deal.name || deal.address}
                    </h3>
                    <p className="mt-1 truncate text-sm text-slate-500">
                      {deal.city}, {deal.state}
                    </p>
                  </div>
                  {goNoGo && (
                    <div
                      className={clsx(
                        'ml-2 flex-shrink-0 rounded-full px-2 py-1 text-xs font-medium',
                        goNoGo.verdict === 'pass' && 'bg-success-100 text-success-700',
                        goNoGo.verdict === 'fail' && 'bg-danger-100 text-danger-700',
                        goNoGo.verdict === 'warning' && 'bg-warning-100 text-warning-700'
                      )}
                    >
                      {goNoGo.verdict === 'pass' && <TrendingUp className="inline h-3 w-3 mr-1" />}
                      {goNoGo.verdict === 'fail' && <TrendingDown className="inline h-3 w-3 mr-1" />}
                      {goNoGo.verdict === 'warning' && <AlertCircle className="inline h-3 w-3 mr-1" />}
                      {goNoGo.verdict.toUpperCase()}
                    </div>
                  )}
                </div>

                <div className="mt-4 grid grid-cols-2 gap-3">
                  <div>
                    <p className="text-xs text-slate-500">Purchase Price</p>
                    <p className="font-mono font-medium text-slate-900">
                      {formatCurrency(deal.purchasePrice)}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs text-slate-500">Monthly Rent</p>
                    <p className="font-mono font-medium text-slate-900">
                      {formatCurrency(deal.monthlyRent)}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs text-slate-500">Cap Rate</p>
                    <p className={clsx(
                      'font-mono font-medium',
                      metrics.capRate >= 8 ? 'text-success-600' : metrics.capRate >= 6 ? 'text-warning-600' : 'text-danger-600'
                    )}>
                      {formatPercent(metrics.capRate)}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs text-slate-500">Cash Flow</p>
                    <p className={clsx(
                      'font-mono font-medium',
                      metrics.monthlyCashFlow >= 200 ? 'text-success-600' : metrics.monthlyCashFlow >= 0 ? 'text-warning-600' : 'text-danger-600'
                    )}>
                      {formatCurrency(metrics.monthlyCashFlow)}/mo
                    </p>
                  </div>
                </div>
              </Link>
            )
          })}
        </div>
      )}
    </div>
  )
}
